import { useMemo, lazy } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateOpenRoutes } from 'actionCreators';
import { selectIntegrationAgentDomainsData } from 'selectors';
import IntegrationSidebar from 'components/IntegrationSidebar';
import {
  getCanSeeSandboxTab,
  getDataManagementPageUrl,
  getCsmToolsPageUrl,
  getIsTenantEnabled,
  getSandboxesPageUrl,
  getSubscriptionRealmsPageUrl,
  getTeamManagementPageUrl,
  getTenantAndRealmManagementPageUrl,
} from 'utils';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { Settings, Tool, Shield, Grid } from 'react-feather';
import {
  integrations,
  REALM_MIGRATION_PATH,
  REPORTS_OVERALL_INTEGRATION_DETAILS_PATH,
  TENANT_PATH,
} from '@constants';
import { RouteType, RouteChildType } from 'types/routes';
import {
  homeRoutes,
  teamRoutes,
  dataDeletionRoutes,
  teamV2Routes,
  membersRoutes,
  pagesRoutes,
  authRoutes,
  PricingToolRoutes,
  activityLogRoutes,
} from '.';
import { useMount } from 'react-use';
import { TenantsContainer } from 'TenantModule/TenantsContainer';

const GeneralIntegration = lazy(
  () => import('../modules/IntegrationModule/General/GeneralIntegrationPage'),
);

const SandboxesPageContainer = lazy(
  () => import('../modules/SandboxModule/SandboxesPageContainer'),
);

const SubscriptionRealmsPageContainer = lazy(
  () =>
    import('../modules/RealmModule/components/SubscriptionRealm/SubscriptionRealmsPageContainer'),
);
const TenantAndRealmManagementPage = lazy(
  () => import('../modules/RealmModule/TenantAndRealmManagement/TenantAndRealmManagementPage'),
);
const RealmMigrationPage = lazy(
  () => import('../modules/RealmModule/RealmMigration/RealmMigrationPage'),
);
const DataManagement = lazy(
  () => import('../modules/ReportsModule/Developer/DataManagement/DataManagementPage'),
);
const TeamManagement = lazy(
  () => import('../modules/ReportsModule/Developer/TeamManagement/TeamManagementPage'),
);
const CsmTools = lazy(() => import('../modules/ReportsModule/Developer/CsmTools/CsmToolsPage'));

export const useRoutes = (): UseRoutesReturn => {
  const { isRequesting: isLoadingAgentDomains, data: agentDomains } = useSelector(
    selectIntegrationAgentDomainsData,
  );
  const dispatch = useDispatch();
  const { isDevToolTabEnabled, shouldEnableIntegrationServerConnection } = useFeatureFlags();

  const devRoutes = useMemo((): RouteType | null => {
    return isDevToolTabEnabled
      ? {
          id: 'Tools',
          icon: <Tool />,
          prod: true,
          children: [
            {
              path: getCsmToolsPageUrl(),
              name: 'CSM Tools',
              component: CsmTools,
              prod: true,
            },
            {
              path: getDataManagementPageUrl(),
              name: 'Data Management',
              component: DataManagement,
              prod: true,
              // disabled: !isDevToolTabEnabled,
            },
            {
              path: getTeamManagementPageUrl(),
              name: 'Team Lifecycle',
              // disabled: !isDevToolTabEnabled,
              component: TeamManagement,
              prod: true,
            },
          ],
        }
      : null;
  }, [isDevToolTabEnabled]);

  const adminRoutes = useMemo(() => {
    return {
      id: 'Administration',
      icon: <Shield />,
      prod: true,
      children: [
        ...(getIsTenantEnabled()
          ? [
              {
                name: 'Tenants',
                prod: true,
                path: TENANT_PATH,
                component: TenantsContainer,
              },
            ]
          : []),
        {
          name: 'Subscriptions',
          prod: true,
          path: getSubscriptionRealmsPageUrl(),
          component: SubscriptionRealmsPageContainer,
        },
        ...(getCanSeeSandboxTab()
          ? [
              {
                name: 'Sandboxes',
                prod: true,
                path: getSandboxesPageUrl(),
                component: SandboxesPageContainer,
              },
            ]
          : []),
        ...(getIsTenantEnabled()
          ? [
              {
                name: 'Tenant & Realm Management',
                prod: true,
                path: getTenantAndRealmManagementPageUrl(),
                component: TenantAndRealmManagementPage,
              },
              // Same gate as the page above: migration is a realm operation, and outside
              // party/prod there are no other tenants to migrate between.
              {
                name: 'Realm Migration',
                prod: true,
                path: REALM_MIGRATION_PATH,
                component: RealmMigrationPage,
              },
            ]
          : []),
      ],
    };
  }, []);

  const integrationRoutes = useMemo(() => {
    // If agentDomains not loaded yet, show all integrations
    // Once loaded, only show integrations that have domains configured
    const integrationRoutesChildren = integrations.reduce<RouteChildType[]>((acc, integration) => {
      // Guard against undefined agentDomains during initial load
      const shouldShowIntegration =
        isLoadingAgentDomains || (agentDomains && agentDomains[integration.id]);

      if (shouldShowIntegration) {
        acc.push({
          path: REPORTS_OVERALL_INTEGRATION_DETAILS_PATH,
          name: '',
          prod: true,
          component: GeneralIntegration,
          integrationId: integration.id,
          CustomSidebarComponent: IntegrationSidebar,
        });
      }

      return acc;
    }, []);

    return {
      id: 'Integrations',
      icon: <Grid />,
      prod: true,
      children: integrationRoutesChildren,
    };
  }, [agentDomains, isLoadingAgentDomains, integrations]);

  const integrationRoutesToUse = useMemo(() => {
    if (shouldEnableIntegrationServerConnection) {
      return [integrationRoutes];
    }
    return [];
  }, [integrationRoutes]);

  const routes = useMemo(
    () =>
      [
        homeRoutes,
        teamRoutes,
        dataDeletionRoutes,
        ...integrationRoutesToUse,
        teamV2Routes,
        membersRoutes,
        PricingToolRoutes,
        activityLogRoutes,
        devRoutes,
        adminRoutes,
        pagesRoutes,
      ].filter((route): route is RouteType => route !== null),
    [adminRoutes, devRoutes, integrationRoutesToUse],
  );

  const sidebarRoutes = useMemo(
    () =>
      [
        homeRoutes,
        teamV2Routes,
        membersRoutes,
        PricingToolRoutes,
        activityLogRoutes,
        teamRoutes,
        devRoutes,
        adminRoutes,
        authRoutes,
        ...integrationRoutesToUse,
      ].filter((route): route is RouteType => route !== null),
    [adminRoutes, devRoutes, integrationRoutesToUse],
  );

  useMount(() => {
    const defaultOpenRoutes = sidebarRoutes.reduce<Record<string, boolean>>((acc, route) => {
      if (route.id) {
        acc[route.id] = true;
      }
      return acc;
    }, {});
    dispatch(updateOpenRoutes(defaultOpenRoutes));
  });

  return { routes, sidebarRoutes };
};

export type UseRoutesReturn = {
  routes: RouteType[];
  sidebarRoutes: RouteType[];
};

export default useRoutes;
